import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { ActKc2GroupTypeEnum, type ActKc2ListItemData } from 'gql/graphql';
import { usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';
import type { TGroupedData, TOrderState } from 'pages/Project/Common/ActsKC2ListContainer/helpers';
import { getGroupByType, getOrderValues, groupIdGetter } from 'pages/Project/Common/ActsKC2ListContainer/helpers';

import type { SortChangedEvent } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { uerWorkOrderActsKC2RegistryGql } from '../uerWorkOrderActsKc2ListQueries';

import type { TFilterFieldsType } from './useUerActsKc2ListColumns';

type TFilterWhere = Record<'and', any>;
export type TGroupedState = { [key: string]: boolean };

const getFilterWhere = (
    fieldName: TFilterFieldsType['fieldName'],
    valuesArr: TFilterFieldsType['checkedValues'],
    prevState: TFilterWhere | undefined,
) => {
    if (!valuesArr?.length && prevState?.and[fieldName]) {
        return { [fieldName]: undefined };
    }

    if (fieldName === 'actDate') {
        if (!valuesArr.length && prevState?.and?.or) {
            return { or: undefined };
        }

        return {
            or: valuesArr.map((item: string) => ({
                [fieldName]: {
                    eq: item,
                },
            })),
        };
    }

    if (fieldName === 'costGroup') {
        if (valuesArr?.length) {
            return {
                [fieldName]: {
                    in: valuesArr,
                },
            };
        }

        return {
            [fieldName]: undefined,
        };
    }

    if (fieldName === 'constructionType.name') {
        const fieldNameSplit = fieldName.split('.')[0];

        if (!valuesArr?.length && prevState?.and?.[fieldNameSplit]) {
            return { [fieldNameSplit]: undefined };
        }

        return {
            [fieldNameSplit]: {
                name: {
                    or: valuesArr?.map((item: string) => ({
                        eq: item,
                    })),
                },
            },
        };
    }

    return {
        [fieldName]: {
            or: valuesArr?.map((item: string) => ({
                eq: item,
            })),
        },
    };
};

export const useGetUerActsKc2List = ({
    workOrderId,
    groupType,
    defaultOpenedSubGroup,
}: {
    workOrderId: number;
    groupType: ActKc2GroupTypeEnum | null | undefined;
    defaultOpenedSubGroup?: string;
}) => {
    const dispatch = useDispatch();
    const [groupState, setGroupState] = useState<TGroupedState>({});

    const [filters, setFilters] = useState<TFilterWhere | undefined>(undefined);
    const [order, setOrder] = useState<TOrderState | TOrderState[] | undefined>(undefined);

    const changeFilterHandler = useCallback<(props: any) => void>(({ checkedValues, fieldName }: TFilterFieldsType) => {
        setFilters((prevValues) => ({
            and: {
                ...prevValues?.and,
                ...getFilterWhere(fieldName, checkedValues, prevValues),
            },
        }));
    }, []);

    const { canSendRequest } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(changeFilterHandler, 'uerActList', () => false),
                withFakeSetter: true,
            }),
            // hack: после обновления типа группировки/сортировки хотим обновить функцию, для обновления canSendRequest
            //  eslint-disable-next-line react-hooks/exhaustive-deps
            [changeFilterHandler, groupType, order],
        ),
    );

    const changeOrderHandler = useCallback(
        (params: SortChangedEvent<TGroupedData<ActKc2ListItemData, ActKc2GroupTypeEnum>>) => {
            const columnsState = params?.api?.getColumnState();

            const { sort, colId } = columnsState?.find((item) => item.sort !== null) ?? {};

            setOrder((prevState) => getOrderValues(sort, colId, groupType, prevState));
        },
        [groupType],
    );

    const changeGroupState = useCallback(
        (id) => () => {
            setGroupState((prevState) => ({ ...prevState, [id]: !prevState[id] ?? false }));
        },
        [],
    );

    const variables = useMemo(
        () => ({
            groupType,
            workOrderId,
            where: filters,
            order,
        }),
        [order, filters, groupType, workOrderId],
    );

    const [listData, getListStatus, , getListRequest] = useRequest({
        requestId: 'uerActsKc2List',
        query: uerWorkOrderActsKC2RegistryGql,
        variables,
        withEffect: !!workOrderId && canSendRequest,
    });

    const isLoading = getListStatus === RequestStatus.FETCHING;

    const groupedItems = useMemo<TGroupedData<ActKc2ListItemData, ActKc2GroupTypeEnum>[]>(() => {
        if (!isLoading) {
            if (groupType) {
                return getGroupByType(
                    listData?.uerWorkOrderActsKC2Registry?.items ?? [],
                    groupType,
                    groupType === ActKc2GroupTypeEnum.ByStatusSkzs,
                );
            }

            return listData?.uerWorkOrderActsKC2Registry?.items ?? [];
        }

        return [];
    }, [groupType, isLoading, listData?.uerWorkOrderActsKC2Registry?.items]);

    useEffect(() => {
        if (groupedItems) {
            const groupIds: TGroupedState = {};

            groupedItems.forEach((item) => {
                if ('isGroup' in item && item.isGroup) {
                    groupIds[item.groupId] = defaultOpenedSubGroup
                        ? item.groupId.toLowerCase() === defaultOpenedSubGroup.toLowerCase()
                        : true;
                }
            });

            setGroupState(groupIds);
        }
    }, [defaultOpenedSubGroup, groupedItems]);

    const actualGroupedItems = useMemo(
        () =>
            groupedItems.filter((item) => {
                if (!('isGroup' in item) && groupType) {
                    return groupState[groupIdGetter(item, groupType)];
                }

                return true;
            }),
        [groupState, groupType, groupedItems],
    );

    const fetchedDataCount = useMemo(
        () =>
            groupType === ActKc2GroupTypeEnum.Custom
                ? groupedItems?.filter((i) => !i.isGroup).length
                : listData?.uerWorkOrderActsKC2Registry?.items?.length ?? 0,
        [groupType, groupedItems, listData?.uerWorkOrderActsKC2Registry?.items?.length],
    );
    const totalItemsCount = useMemo(
        () =>
            groupType === ActKc2GroupTypeEnum.Custom
                ? groupedItems?.filter((i) => !i.isGroup).length
                : listData?.uerWorkOrderActsKC2Registry?.totalCount ?? 0,
        [groupType, groupedItems, listData?.uerWorkOrderActsKC2Registry?.totalCount],
    );

    const refreshActListData = useCallback(() => {
        dispatch(getListRequest(variables));
    }, [dispatch, getListRequest, variables]);

    return {
        actKc2List: actualGroupedItems,
        totalItemsCount,
        fetchedDataCount,
        refreshActListData,
        isLoading,
        groupState,
        changeGroupState,
        changeFilterHandler,
        changeOrderHandler,
    };
};
